/**
 * FO-304 + FO-308 (batch-33) — gedeelde klantenportaal-schrijf-schemas.
 *
 * Eén bron voor de klant-API én de klant-app-forms (inline veldfouten), conform
 * de validatie-standaard. Alle redenen min. 10 tekens, max. 2000.
 */
import { z } from 'zod'

const reden = z
  .string()
  .trim()
  .min(10, 'Geef een reden van minstens 10 tekens')
  .max(2000, 'Maximaal 2000 tekens')

// ── FO-304-B — klant-reactie op een afwezigheidsmelding ──────────────────────
export const KLANT_AFWEZIGHEID_OORDEEL = ['AKKOORD', 'BEZWAAR'] as const
export type KlantAfwezigheidOordeel = (typeof KLANT_AFWEZIGHEID_OORDEEL)[number]

export const klantAfwezigheidReactieSchema = z.object({
  meldingId: z.string().min(8).max(64),
  oordeel: z.enum(KLANT_AFWEZIGHEID_OORDEEL),
  reden,
})
export type KlantAfwezigheidReactieInput = z.infer<typeof klantAfwezigheidReactieSchema>

// ── FO-304-C — geschiktheids-feedback op een monteur ─────────────────────────
export const KLANT_MONTEUR_OORDEEL = ['GESCHIKT', 'LIEVER_NIET'] as const
export type KlantMonteurOordeelValue = (typeof KLANT_MONTEUR_OORDEEL)[number]

export const klantMonteurFeedbackSchema = z.object({
  employeeId: z.string().min(1),
  oordeel: z.enum(KLANT_MONTEUR_OORDEEL),
  reden,
})
export type KlantMonteurFeedbackInput = z.infer<typeof klantMonteurFeedbackSchema>

// ── FO-304-F — reactie (opmerking) op een werkbon of factuur ─────────────────
export const KLANT_DOCUMENT_BRON = ['WERKBON', 'FACTUUR'] as const
export type KlantDocumentBron = (typeof KLANT_DOCUMENT_BRON)[number]

export const klantDocumentReactieSchema = z.object({
  tekst: z.string().trim().min(1, 'Bericht mag niet leeg zijn').max(2000),
  parentId: z.string().min(1).optional(),
})
export type KlantDocumentReactieInput = z.infer<typeof klantDocumentReactieSchema>

// ── FO-308-P2a — offerte-besluit door de klant ───────────────────────────────
export const klantOfferteBesluitSchema = z.discriminatedUnion('actie', [
  z.object({ actie: z.literal('AKKOORD'), versie: z.number().int().positive() }),
  z.object({
    actie: z.literal('AFWIJZEN'),
    versie: z.number().int().positive(),
    reden,
  }),
])
export type KlantOfferteBesluitInput = z.infer<typeof klantOfferteBesluitSchema>

// ── FO-308-P2c — storing melden ──────────────────────────────────────────────
export const STORING_URGENTIE = ['LAAG', 'NORMAAL', 'HOOG', 'KRITIEK'] as const
export type StoringUrgentie = (typeof STORING_URGENTIE)[number]

export const klantStoringSchema = z.object({
  omschrijving: z.string().trim().min(10, 'Beschrijf de storing (min. 10 tekens)').max(5000),
  urgentie: z.enum(STORING_URGENTIE),
  installatieId: z.string().min(1).optional(),
  locatie: z.string().max(200).optional(),
})
export type KlantStoringInput = z.infer<typeof klantStoringSchema>

// ── FO-308-P2d — zelfbeheer gebruikers (OWNER) ───────────────────────────────
export const KLANT_ZELFBEHEER_ROLLEN = ['ADMIN', 'FINANCE', 'READONLY'] as const
export type KlantZelfbeheerRol = (typeof KLANT_ZELFBEHEER_ROLLEN)[number]

export const klantGebruikerUitnodigenSchema = z.object({
  naam: z.string().trim().min(2, 'Naam is verplicht').max(120),
  email: z.string().trim().email('Ongeldig e-mailadres'),
  rol: z.enum(KLANT_ZELFBEHEER_ROLLEN),
})
export type KlantGebruikerUitnodigenInput = z.infer<typeof klantGebruikerUitnodigenSchema>

export const klantGebruikerRolSchema = z.object({
  rol: z.enum(KLANT_ZELFBEHEER_ROLLEN),
})

// ── FO-308-P2e — eigen notificatie-voorkeuren ────────────────────────────────
export const KLANT_NOTIFICATIE_CATEGORIE = [
  'WERKBON',
  'FACTUUR',
  'PLANNING_AFWEZIGHEID',
  'CONTRACT_AFLOOP',
  'STORING',
  'OFFERTE',
] as const
export type KlantNotificatieCategorie = (typeof KLANT_NOTIFICATIE_CATEGORIE)[number]

export const klantNotificatieVoorkeurSchema = z.object({
  categorie: z.enum(KLANT_NOTIFICATIE_CATEGORIE),
  enabled: z.boolean(),
})
export type KlantNotificatieVoorkeurInput = z.infer<typeof klantNotificatieVoorkeurSchema>
